import { useState } from 'react';
import { entityRegistry, getEntitiesGroupedByCategory, EntityCategory } from '@/config/entities';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Database, Columns, Filter, Shield, Bell, Settings2, ChevronDown, LayoutGrid } from 'lucide-react';
import { EntityManager } from '@/components/entity/EntityManager';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const CATEGORY_LABELS: Record<EntityCategory, string> = {
  business: 'Business Entities',
  master: 'Master Data',
  system: 'System Entities',
};

const EntitySchemaViewer = () => {
  const groupedEntities = getEntitiesGroupedByCategory();
  const [selectedEntity, setSelectedEntity] = useState<string>(() => {
    // Default to first business entity or first available
    const firstBusiness = groupedEntities.business[0];
    return firstBusiness?.name || Object.keys(entityRegistry)[0] || '';
  });
  const [openCategories, setOpenCategories] = useState<Record<EntityCategory, boolean>>({
    business: true,
    master: true,
    system: true,
  });
  
  const config = entityRegistry[selectedEntity];

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No entities configured</p>
      </div>
    );
  }

  const toggleCategory = (category: EntityCategory) => {
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Entity Schema</h1>
        <p className="text-muted-foreground">View schemas and manage entity data</p>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Entity List - Grouped by Category */}
        <Card className="p-4">
          <h3 className="font-semibold mb-4">Entities</h3>
          <div className="space-y-3">
            {(Object.entries(groupedEntities) as [EntityCategory, typeof groupedEntities.business][])
              .filter(([_, entities]) => entities.length > 0)
              .map(([category, entities]) => (
                <Collapsible 
                  key={category} 
                  open={openCategories[category]}
                  onOpenChange={() => toggleCategory(category)}
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
                    <span>{CATEGORY_LABELS[category]}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${openCategories[category] ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 mt-1">
                    {entities.map(({ name, config: cfg }) => (
                      <button
                        key={name}
                        onClick={() => setSelectedEntity(name)}
                        className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          selectedEntity === name 
                            ? 'bg-primary text-primary-foreground' 
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {cfg.icon}
                          <span className="text-sm">{cfg.pluralName}</span>
                        </div>
                      </button>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ))}
          </div>
        </Card>

        {/* Entity Details */}
        <div className="col-span-3">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-6">
              {config.icon}
              <div>
                <h2 className="text-2xl font-bold">{config.pluralName}</h2>
                <p className="text-muted-foreground">Table: {config.tableName}</p>
              </div>
              <div className="ml-auto flex gap-2">
                {config.category && (
                  <Badge variant="outline" className="capitalize">
                    {config.category}
                  </Badge>
                )}
                <Badge variant="outline">
                  {config.permissionModule}
                </Badge>
              </div>
            </div>

            <Tabs defaultValue="manage">
              <TabsList>
                <TabsTrigger value="manage" className="flex items-center gap-2">
                  <LayoutGrid className="h-4 w-4" />
                  Manage Data
                </TabsTrigger>
                <TabsTrigger value="columns" className="flex items-center gap-2">
                  <Columns className="h-4 w-4" />
                  Columns
                </TabsTrigger>
                <TabsTrigger value="fields" className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Form Fields
                </TabsTrigger>
                <TabsTrigger value="filters" className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                </TabsTrigger>
                <TabsTrigger value="permissions" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Config
                </TabsTrigger>
                <TabsTrigger value="events" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Events
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manage" className="mt-4">
                <EntityManager 
                  key={selectedEntity}
                  entityName={selectedEntity} 
                />
              </TabsContent>

              <TabsContent value="columns" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Header</TableHead>
                      <TableHead>Accessor</TableHead>
                      <TableHead>Sortable</TableHead>
                      <TableHead>Visible</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config.columns.map((col) => (
                      <TableRow key={col.id}>
                        <TableCell className="font-mono text-sm">{col.id}</TableCell>
                        <TableCell>{col.header}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {col.accessorKey ? String(col.accessorKey) : col.accessorFn ? 'fn()' : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={col.sortable ? 'default' : 'secondary'}>
                            {col.sortable ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={col.defaultVisible !== false ? 'default' : 'secondary'}>
                            {col.defaultVisible !== false ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="fields" className="mt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Label</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Required</TableHead>
                      <TableHead>Grid Span</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config.formFields.map((field) => (
                      <TableRow key={field.name}>
                        <TableCell className="font-mono text-sm">{field.name}</TableCell>
                        <TableCell>{field.label}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{field.type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={field.required ? 'default' : 'secondary'}>
                            {field.required ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        <TableCell>{field.gridSpan || 1}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="filters" className="mt-4">
                {config.filters && config.filters.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Field</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Options</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {config.filters.map((filter) => (
                        <TableRow key={filter.id}>
                          <TableCell className="font-mono text-sm">{filter.id}</TableCell>
                          <TableCell>{filter.label}</TableCell>
                          <TableCell className="font-mono text-sm">{filter.field}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{filter.type}</Badge>
                          </TableCell>
                          <TableCell>{filter.options?.length || 0} options</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No filters configured</p>
                )}
              </TabsContent>

              <TabsContent value="permissions" className="mt-4">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Permissions
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Module:</span>
                          <Badge>{config.permissionModule}</Badge>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Settings2 className="h-4 w-4" />
                        Features
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Can Create:</span>
                          <Badge variant={config.features?.canCreate !== false ? 'default' : 'secondary'}>
                            {config.features?.canCreate !== false ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Can Update:</span>
                          <Badge variant={config.features?.canUpdate !== false ? 'default' : 'secondary'}>
                            {config.features?.canUpdate !== false ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Can Delete:</span>
                          <Badge variant={config.features?.canDelete !== false ? 'default' : 'secondary'}>
                            {config.features?.canDelete !== false ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Bulk Delete:</span>
                          <Badge variant={config.features?.canBulkDelete !== false ? 'default' : 'secondary'}>
                            {config.features?.canBulkDelete !== false ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  </div>

                  <Card className="p-4">
                    <h4 className="font-semibold mb-3">Audit Configuration</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Audit Enabled:</span>
                        <Badge variant={config.audit?.enabled ? 'default' : 'secondary'}>
                          {config.audit?.enabled ? 'Yes' : 'No'}
                        </Badge>
                      </div>
                      {config.audit?.excludeFields && config.audit.excludeFields.length > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Excluded Fields:</span>
                          <span className="font-mono text-xs">
                            {config.audit.excludeFields.join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="events" className="mt-4">
                <div className="space-y-4">
                  {config.notifications?.onCreate && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          onCreate
                        </Badge>
                        <span>→</span>
                        <Badge>{config.notifications.onCreate.eventType}</Badge>
                        {config.notifications.onCreate.recipients && (
                          <span className="text-sm text-muted-foreground ml-2">
                            Recipients: {Array.isArray(config.notifications.onCreate.recipients) 
                              ? config.notifications.onCreate.recipients.join(', ')
                              : config.notifications.onCreate.recipients}
                          </span>
                        )}
                      </div>
                    </Card>
                  )}
                  {config.notifications?.onUpdate && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          onUpdate
                        </Badge>
                        <span>→</span>
                        <Badge>{config.notifications.onUpdate.eventType}</Badge>
                        {config.notifications.onUpdate.recipients && (
                          <span className="text-sm text-muted-foreground ml-2">
                            Recipients: {Array.isArray(config.notifications.onUpdate.recipients) 
                              ? config.notifications.onUpdate.recipients.join(', ')
                              : config.notifications.onUpdate.recipients}
                          </span>
                        )}
                      </div>
                    </Card>
                  )}
                  {config.notifications?.onDelete && (
                    <Card className="p-4">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          onDelete
                        </Badge>
                        <span>→</span>
                        <Badge>{config.notifications.onDelete.eventType}</Badge>
                      </div>
                    </Card>
                  )}
                  {!config.notifications?.onCreate && !config.notifications?.onUpdate && !config.notifications?.onDelete && (
                    <p className="text-muted-foreground text-center py-8">No event notifications configured</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EntitySchemaViewer;
